import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [body]
 * @property {HTMLFormElement} [form]
 * @property {HTMLElement} [productTitle]
 * @property {HTMLElement} [variantWrap]
 * @property {HTMLElement} [variantLabel]
 * @property {HTMLInputElement} [email]
 * @property {HTMLElement} [error]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [success]
 * @property {HTMLElement} [successHeading]
 * @property {HTMLElement} [successText]
 * @property {HTMLButtonElement} [closeButton]
 */

/**
 * @typedef {object} BackInStockOpenOptions
 * @property {number | string} productId
 * @property {number | string} variantId
 * @property {string} [productTitle]
 * @property {string} [variantLabel]
 */

/**
 * @typedef {object} BisResponse
 * @property {string} status
 * @property {string} [message]
 * @property {Record<string, string[]>} [errors]
 */

/**
 * @typedef {object} BisApi
 * @property {(email: string | null, variantId: number, productId: number, attributes?: object) => { then: (cb: (data: BisResponse) => void) => void }} create
 * @property {{ show?: Function, hide?: Function }} [popup]
 */

/**
 * Theme drawer with a native notify form powered by AMP `BIS.create`.
 * @extends {DialogComponent}
 */
class BackInStockDrawer extends DialogComponent {
  /** @type {number | null} */
  #productId = null;

  /** @type {number | null} */
  #variantId = null;

  /** @type {string} */
  #submitDefaultLabel = '';

  connectedCallback() {
    super.connectedCallback();

    const { dialog, submit } = this.refs;
    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener('close', this.#onDialogClose);
    }

    if (submit instanceof HTMLButtonElement) {
      this.#submitDefaultLabel = submit.textContent?.trim() ?? '';
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { dialog } = this.refs;
    if (dialog instanceof HTMLDialogElement) {
      dialog.removeEventListener('close', this.#onDialogClose);
    }
  }

  /**
   * Opens the drawer and fills product context for the notify form.
   * @param {BackInStockOpenOptions} [options]
   */
  openDrawer(options = {}) {
    const productId = Number.parseInt(String(options.productId ?? ''), 10);
    const variantId = Number.parseInt(String(options.variantId ?? ''), 10);

    this.#productId = Number.isFinite(productId) && productId > 0 ? productId : null;
    this.#variantId = Number.isFinite(variantId) && variantId > 0 ? variantId : null;

    this.dataset.productId = this.#productId ? String(this.#productId) : '';
    this.dataset.variantId = this.#variantId ? String(this.#variantId) : '';

    this.#resetUi();
    this.#fillContext(options);
    this.#hideAmpPopup();

    const { dialog } = this.refs;
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      this.showDialog();
    }

    requestAnimationFrame(() => {
      const title = this.querySelector('#back-in-stock-modal-title');
      if (title instanceof HTMLElement) title.focus();
    });
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { email, submit, form, successHeading, successText } = this.refs;
    if (!(email instanceof HTMLInputElement) || !(form instanceof HTMLFormElement)) return;

    this.#clearError();

    const emailValue = email.value.trim();
    if (!emailValue || !email.checkValidity()) {
      this.#showError(
        this.dataset.labelInvalidEmail || 'Please enter a valid email address.',
        email
      );
      return;
    }

    if (!this.#productId || !this.#variantId) {
      this.#showError(this.dataset.labelUnavailable || 'Notification is unavailable right now.');
      return;
    }

    const bis = await this.#waitForBis();
    if (!bis?.create) {
      this.#showError(this.dataset.labelUnavailable || 'Notification is unavailable right now.');
      return;
    }

    if (submit instanceof HTMLButtonElement) {
      submit.disabled = true;
      submit.textContent = this.dataset.labelSubmitting || 'Sending…';
    }

    try {
      const response = await this.#createNotification(bis, emailValue);

      if (response?.status === 'OK') {
        this.#setView('success');

        if (successText instanceof HTMLElement) {
          successText.textContent =
            this.dataset.labelSuccess ||
            "We'll email you when this product is back in stock.";
        }

        requestAnimationFrame(() => {
          if (successHeading instanceof HTMLElement) {
            successHeading.focus();
            successHeading.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
        return;
      }

      this.#showError(this.#formatErrors(response), email);
    } catch (_error) {
      this.#showError(this.dataset.labelUnavailable || 'Notification is unavailable right now.');
    } finally {
      if (submit instanceof HTMLButtonElement) {
        submit.disabled = false;
        submit.textContent = this.#submitDefaultLabel;
      }
    }
  }

  /**
   * @param {Event} [_event]
   */
  #onDialogClose = (_event) => {
    this.#resetUi();
    this.#hideAmpPopup();
  };

  /**
   * @param {BackInStockOpenOptions} options
   */
  #fillContext(options) {
    const { productTitle, variantWrap, variantLabel } = this.refs;
    const title = (options.productTitle || '').trim();
    const label = (options.variantLabel || '').trim();

    if (productTitle instanceof HTMLElement) {
      productTitle.textContent = title;
      productTitle.hidden = !title;
    }

    if (variantWrap instanceof HTMLElement && variantLabel instanceof HTMLElement) {
      variantLabel.textContent = label;
      variantWrap.hidden = !label;
    }
  }

  #resetUi() {
    const { form, email, error, successText, submit } = this.refs;

    this.#setView('form');

    if (form instanceof HTMLFormElement) {
      form.reset();
    }

    if (email instanceof HTMLInputElement) {
      email.removeAttribute('aria-invalid');
    }

    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }

    if (successText instanceof HTMLElement) {
      successText.textContent = '';
    }

    if (submit instanceof HTMLButtonElement) {
      submit.disabled = false;
      submit.textContent = this.#submitDefaultLabel;
    }
  }

  /**
   * @param {'form' | 'success'} view
   */
  #setView(view) {
    const { body, form, success } = this.refs;

    if (body instanceof HTMLElement) {
      body.dataset.state = view;
    }

    if (form instanceof HTMLFormElement) {
      form.hidden = view !== 'form';
    }

    if (success instanceof HTMLElement) {
      success.hidden = view !== 'success';
    }
  }

  /**
   * @param {string} message
   * @param {HTMLInputElement} [email]
   */
  #showError(message, email) {
    const { error } = this.refs;

    if (error instanceof HTMLElement) {
      error.hidden = false;
      error.textContent = message;
    }

    if (email instanceof HTMLInputElement) {
      email.setAttribute('aria-invalid', 'true');
      email.focus();
    }
  }

  #clearError() {
    const { email, error } = this.refs;

    if (email instanceof HTMLInputElement) {
      email.removeAttribute('aria-invalid');
    }

    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }
  }

  /**
   * @param {BisResponse | null | undefined} response
   * @returns {string}
   */
  #formatErrors(response) {
    if (response?.message) return response.message;

    const errors = response?.errors;
    if (!errors || typeof errors !== 'object') {
      return this.dataset.labelUnavailable || 'Notification is unavailable right now.';
    }

    const parts = [];
    for (const [field, messages] of Object.entries(errors)) {
      if (Array.isArray(messages) && messages.length) {
        parts.push(`${field}: ${messages.join(', ')}`);
      }
    }

    return parts.join(' ') || this.dataset.labelUnavailable || 'Notification is unavailable right now.';
  }

  /**
   * @returns {Promise<BisApi | null>}
   */
  async #waitForBis() {
    for (let attempt = 0; attempt < 40; attempt++) {
      const bis = this.#getBisApi();
      if (bis?.create) return bis;

      await new Promise((resolve) => {
        setTimeout(resolve, 150);
      });
    }

    return null;
  }

  /**
   * @returns {BisApi | null}
   */
  #getBisApi() {
    const candidates = [
      /** @type {BisApi | undefined} */ (window.BIS),
      /** @type {BisApi | undefined} */ (window['BackInStock']),
    ];
    for (const candidate of candidates) {
      if (candidate && typeof candidate.create === 'function') {
        return candidate;
      }
    }
    return null;
  }

  #hideAmpPopup() {
    const bis = this.#getBisApi();
    if (bis?.popup && typeof bis.popup.hide === 'function') {
      bis.popup.hide();
    }
  }

  /**
   * AMP's promise is not always a native Promise — normalize it.
   * @param {BisApi} bis
   * @param {string} email
   * @returns {Promise<BisResponse>}
   */
  #createNotification(bis, email) {
    const productId = this.#productId;
    const variantId = this.#variantId;

    if (!productId || !variantId) {
      return Promise.reject(new Error('Missing product context'));
    }

    return new Promise((resolve, reject) => {
      try {
        const result = bis.create(email, variantId, productId);
        if (result && typeof result.then === 'function') {
          result.then((data) => resolve(data || { status: 'Error' }));
          return;
        }

        reject(new Error('BIS.create did not return a thenable'));
      } catch (error) {
        reject(error);
      }
    });
  }
}

if (!customElements.get('back-in-stock-drawer')) {
  customElements.define('back-in-stock-drawer', BackInStockDrawer);
}
